import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useI18n, translateTimeline } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { StatusChip, fmtDate, docProtocol } from "../lib/format";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Search, Package, Truck, CheckCircle2, RotateCcw, Download, History } from "lucide-react";

export default function ClientPortal() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const historyRequest = useRef(0);

  useEffect(() => {
    (async () => {
      const qs = q ? `?q=${encodeURIComponent(q)}` : "";
      const { data } = await api.get(`/documents${qs}`);
      setDocs(data);
    })();
  }, [q]);

  const openDoc = async (doc) => {
    const requestId = ++historyRequest.current;
    setSelected(doc);
    setLoadingHistory(true);
    try {
      const { data } = await api.get(`/documents/${doc.id}`);
      if (historyRequest.current !== requestId) return;
      setSelected(data);
    } catch {
      if (historyRequest.current !== requestId) return;
    } finally {
      if (historyRequest.current === requestId) setLoadingHistory(false);
    }
  };

  const total = docs.length;
  const received = docs.filter((d) => d.status === "received" || d.status === "sorting").length;
  const delivered = docs.filter((d) => d.status === "delivered").length;
  const inRoute = docs.filter((d) => d.status === "out_for_delivery").length;
  const attempted = docs.filter((d) => d.status === "attempted").length;
  const returned = docs.filter((d) => d.status === "returned").length;

  const kpis = [
    { icon: Package, label: "Total", value: total, bg: "bg-[var(--brand-soft)] text-[var(--brand)]" },
    { icon: Download, label: "Objeto Recebido", value: received, bg: "bg-[var(--success-soft)] text-[var(--success-text)]" },
    { icon: Truck, label: "Em Rota de Entrega", value: inRoute, bg: "bg-[var(--warning-soft)] text-[var(--warning-text)]" },
    { icon: Truck, label: "Tentativas de Entrega", value: attempted, bg: "bg-[var(--warning-soft)] text-[var(--warning-text)]" },
    { icon: CheckCircle2, label: "Entregues", value: delivered, bg: "bg-[var(--success)] text-[var(--surface)]" },
    { icon: RotateCcw, label: "Devolvidos", value: returned, bg: "bg-[var(--danger-soft)] text-[var(--danger)]" },
  ];

  const history = [...(selected?.history || [])].reverse();

  return (
    <div className="space-y-4">
      <div className="card-soft px-5 py-4">
        <div className="text-xs text-[var(--text-2)] font-medium">{t("portal_title")}</div>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-0.5">Olá, {user?.name?.split(" ")[0]}</h1>
        <p className="text-sm text-[var(--text-2)] mt-1">{t("portal_subtitle")}</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {kpis.map((k, i) => (
          <div key={i} className="card-soft p-5">
            <div className={`w-10 h-10 rounded-2xl ${k.bg} flex items-center justify-center mb-3`}>
              <k.icon size={18} />
            </div>
            <div className="text-xs text-[var(--text-2)] font-medium">{k.label}</div>
            <div className="font-display text-3xl font-bold mt-1">{k.value}</div>
          </div>
        ))}
      </div>

      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-3)]" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search_placeholder")} className="pl-9 h-11 rounded-2xl" data-testid="portal-search" />
      </div>

      <div className="card-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--surface-2)] border-b border-[var(--border-c)]">
              <tr className="text-left text-xs text-[var(--text-2)] font-semibold">
                <th className="px-4 py-3">{t("dlg_protocol_number")}</th>
                <th className="px-4 py-3">{t("recipient")}</th>
                <th className="px-4 py-3">{t("city")}</th>
                <th className="px-4 py-3">{t("status")}</th>
                <th className="px-4 py-3">{t("updated")}</th>
              </tr>
            </thead>
            <tbody>
              {docs.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-[var(--text-2)]">{t("no_data")}</td></tr>}
              {docs.map((d) => (
                <tr
                  key={d.id}
                  className="border-b border-[var(--border-c)] hover:bg-[var(--surface-2)] cursor-pointer"
                  onClick={() => openDoc(d)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openDoc(d);
                    }
                  }}
                  tabIndex={0}
                  data-testid={`portal-doc-${docProtocol(d)}`}
                >
                  <td className="px-4 py-3 font-mono text-xs">{docProtocol(d)}</td>
                  <td className="px-4 py-3 font-medium">{d.recipient_name}</td>
                  <td className="px-4 py-3 text-[var(--text-2)]">{d.city}</td>
                  <td className="px-4 py-3"><StatusChip status={d.status} t={t} /></td>
                  <td className="px-4 py-3 font-mono text-xs text-[var(--text-2)]">{fmtDate(d.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(open) => { if (!open) setSelected(null); }}>
        <DialogContent className="max-w-lg rounded-2xl max-h-[85vh] overflow-y-auto" data-testid="portal-doc-history">
          <DialogHeader>
            <DialogTitle className="font-display font-mono">{selected ? docProtocol(selected) : ""}</DialogTitle>
            <DialogDescription>{t("portal_readonly")}</DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-[var(--text-2)]">{t("recipient")}</div>
                  <div className="font-medium mt-0.5">{selected.recipient_name || "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-[var(--text-2)]">{t("status")}</div>
                  <div className="mt-1"><StatusChip status={selected.status} t={t} /></div>
                </div>
                <div className="col-span-2">
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-[var(--text-2)]">{t("address")}</div>
                  <div className="font-medium mt-0.5">
                    {[selected.address, selected.neighborhood, selected.city, selected.state].filter(Boolean).join(", ") || "—"}
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center gap-1.5 text-xs font-semibold text-[var(--text-2)] mb-2">
                  <History size={13} /> {t("history")}
                </div>
                {loadingHistory && history.length === 0 && (
                  <p className="text-sm text-[var(--text-2)]">{t("timeline_loading")}</p>
                )}
                {!loadingHistory && history.length === 0 && (
                  <p className="text-sm text-[var(--text-2)]">{t("timeline_empty")}</p>
                )}
                <div className="space-y-2">
                  {history.map((h, i) => (
                    <div key={`${h.at || i}-${h.status || ""}`} className="flex items-start justify-between gap-2 text-xs rounded-xl bg-[var(--surface-2)] px-3 py-2">
                      <div>
                        <StatusChip status={h.status} t={t} />
                        {h.note && <div className="text-[var(--text-2)] mt-1">{translateTimeline(t, "note", h.note)}</div>}
                      </div>
                      <span className="font-mono text-[var(--text-3)] shrink-0">{fmtDate(h.at)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
